/**
 * Plan 123 — what is still unknown about one home's system, and the operator
 * path for asking the homeowner about it.
 *
 * Five routes on device-data-api, all scoped to the device in the URL:
 *
 *   GET  /devices/:id/equipment-needs                      read_devices
 *   POST /devices/:id/equipment-request/draft              contact_users
 *   POST /devices/:id/equipment-request                    contact_users
 *   GET  /devices/:id/equipment-request/:request_id        contact_users
 *   POST /devices/:id/equipment-request/:request_id/reply  contact_users
 *   POST /devices/:id/equipment-request/resubscribe        contact_users (R13, FS-1021)
 *   POST /devices/:id/verification-email                   contact_users (FS-789)
 *
 * The shapes here are the HTTP contract verbatim. Two of them matter more
 * than the rest:
 *
 * THE CLAIM MARKER. A draft comes back with `system_key`, `claim_id` and
 * `observed_at`, and a send echoes all three back byte for byte. They are the
 * drift check: the projection upsert keeps the claim id while changing the
 * address, so the id alone would not notice a message going to a new owner.
 * Nothing in the console composes them — they are carried, not computed.
 *
 * THE REQUEST ID. The console mints it when the confirm dialog opens and
 * sends the same one on every retry, so a double-click, a refused response
 * and a timed-out one all name the same request. A send that never came back
 * is resolved by READING it (`fetchRequestResult`), never by sending again
 * with a new id.
 *
 * The client never sends an address, an owner or a sibling device list. The
 * API resolves the recipient and the siblings from the current claim, and
 * rejects them if they are supplied.
 */

import axiosInstance from '../utils/axiosInstance';
import config from '../utils/config';
import { easternDate, easternDateTime } from '../utils/easternTime';
import { own } from '../utils/ownKey';

/**
 * Writing to a homeowner is a different act from correcting their record, so
 * it is a different permission (§8.1). The console keeps no static permission
 * registry — accounts-api serves the catalog — so the name lives next to the
 * calls it guards, the way `EDIT_EQUIPMENT_PERMISSION` does.
 */
export const CONTACT_USERS_PERMISSION = 'contact_users';

/**
 * The components a system can be asked about (§3.1). FS-838: `indoor_coil`
 * is presented only while the coil is the system's open tonnage ask (a
 * furnace whose coil has had no photo and no catalog lookup).
 */
export type ComponentName = 'outdoor_unit' | 'indoor_unit' | 'indoor_coil' | 'filter' | 'package_unit';

/**
 * §3.2, plus the `requested` the endpoint overlays from the request history.
 * `requested` is still unknown information: it counts in the score exactly as
 * `needed` does, and the only thing it changes is that nobody asks twice.
 */
export type ComponentState =
  | 'documented'
  | 'needed'
  | 'requested'
  | 'not_applicable'
  | 'blocked';

/**
 * A reason something is refused. The needs endpoint spells it `code`; the
 * Lambda's own draft preview spells the same vocabulary `refusal`. Read both
 * through `blockerCode` rather than picking one.
 */
export interface Blocker {
  code?: string;
  refusal?: string;
  message: string;
  /**
   * Plan 123 R2 F17 — the machine-readable half of a blocker whose sentence
   * names a date. `since` is when the condition started (the ask, the reply),
   * `until` is when it lapses, both UTC ISO. They are absent on a blocker
   * with no such moment and on an API that predates them, so they are read
   * defensively: used when they are there, and the server's own sentence
   * stands when they are not.
   */
  since?: string;
  until?: string;
  /**
   * Plan 123 R3 §3 — the machine-readable half of a blocker whose sentence
   * names a Clip. The server's own message never embeds an id or a date;
   * this screen composes both from the structured fields, which is also what
   * lets the API redact a blocker it must not show (`device_id` to null and
   * one fixed sentence). Absent on an API that predates them.
   */
  device_id?: number | null;
  category?: string | null;
}

export const blockerCode = (blocker: Blocker): string =>
  blocker.code ?? blocker.refusal ?? 'unknown';

/**
 * Two blockers are the same blocker only when they name the same condition,
 * on the same target, over the same dates. That is the Lambda's own identity
 * for one (`dedupe_blockers`: refusal, device id, category, since, until).
 *
 * R4 F31: keying on the code and the Clip alone made two SHARED holds — one
 * on the outdoor unit until October 7, one on the furnace until October 10 —
 * the same key, and the second one never reached the screen.
 */
export const blockerKey = (blocker: Blocker): string =>
  [
    blockerCode(blocker),
    blocker.device_id ?? '',
    blocker.category ?? '',
    blocker.since ?? '',
    blocker.until ?? '',
  ].join('|');

/** One fact a component establishes, with the inventory's own wording for it. */
export interface ComponentFact {
  field: string;
  quality: string;
  fact_state: string;
  known: boolean;
  value: string | null;
  unit: string | null;
  route: string;
  route_reason: string;
  evidence: string | null;
}

/** The open request against one component, when there is one. */
export interface ComponentRequest {
  request_id: string;
  requested_at: string | null;
  sent_at: string | null;
  cooldown_until: string | null;
  state: 'requested' | 'answered' | 'expired';
  reply_outcome: string | null;
  replied_at: string | null;
}

/**
 * Plan 123 R11 — one thing an operator should know before asking, which
 * stops nothing.
 *
 * Two shapes on the wire. The inventory's leftover route reasons have always
 * arrived as bare strings; the R11 notes arrive as the `Blocker` shape, so a
 * note carries its code, its Clip and its dates the way a blocker does.
 */
export type ComponentNote = Blocker | string;

export interface NeedsComponent {
  component: ComponentName;
  state: ComponentState;
  reason: string | null;
  counts_toward_score: boolean;
  /**
   * Plan 123 R11 — always true, and not read any more.
   *
   * R7 made this the server's answer to "would a homeowner photo settle this
   * part", and the console shut the box whenever it was false. R11 takes that
   * decision back: the operator decides what to ask for and when, so the API
   * sets it true on every component and nothing here reads it. It stays on
   * the type for one release so an older tab still renders.
   */
  askable?: boolean;
  facts: ComponentFact[];
  fields: string[];
  /**
   * Plan 123 R11 — what to know before asking for this part: it is already
   * on file, it was asked for a week ago, the homeowner declined. None of
   * them stops anything; a reason a send CANNOT go is `send_blockers`.
   */
  notes?: ComponentNote[];
  /** Phase 4: the open request, or null. */
  request?: ComponentRequest | null;
  /**
   * Plan 123 R11 — why the server would REFUSE a send for this target. Only
   * the hard codes reach this list; everything an operator may decide for
   * themselves is a note.
   */
  send_blockers?: Blocker[];
}

export interface NeedsClip {
  device_id: number;
  device_name: string;
  is_subject: boolean;
  claim_status: string;
  projection_updated_at: string | null;
  shared_record: boolean;
  score: number;
  components: NeedsComponent[];
}

/** One target of a past request, as the history reports it. */
export interface RequestTarget {
  device_id: number | null;
  category: string;
  component: ComponentName;
  reply_outcome: string | null;
  replied_at: string | null;
}

/** The send attempt behind a request, when it got that far. */
export interface RequestSend {
  state: string;
  sending_at: string | null;
  sent_at: string | null;
  send_error: string | null;
  message_id: string | null;
  resolution: string | null;
}

/** One SES event for a send, in the order they arrived. */
export interface RequestEvent {
  type: string;
  at: string;
}

export interface RequestHistoryRow {
  request_id: string;
  status: string;
  requested_at: string | null;
  /** The asking console account's id (`users.id`, as text). */
  requested_by: string | null;
  /**
   * FS-863 — that account's sign-in email; null when the account has been
   * deleted. Absent on an API from before FS-863.
   */
  requested_by_email?: string | null;
  components: ComponentName[];
  device_ids: number[];
  filter_device_ids: number[];
  subject: string | null;
  refusal: string | null;
  send: RequestSend | null;
  events: RequestEvent[];
  targets: RequestTarget[];
  /** Only with contact_users. */
  to_address?: string | null;
}

/**
 * FS-646 — where confirming the homeowner's address has got to, computed by
 * the API from `email_address_status` and `email_verification_sends`. The
 * states are in the order the API tests them; the first match wins.
 */
export type VerificationState =
  | 'owner_confirmed'
  | 'provider_confirmed'
  | 'confirmed_by_delivery'
  | 'internal'
  | 'link_sent'
  | 'no_reminders_left'
  | 'not_asked';

export interface RecipientVerification {
  state: VerificationState;
  /** When the confirmation was recorded (UTC ISO), for the Firebase states. */
  confirmed_at: string | null;
  /** The earliest verification mail, the app's own ask included. */
  first_sent_at: string | null;
  last_sent_at: string | null;
  /** Server reminders that went out, 0 to 2. */
  server_reminders_sent: number;
  /** When the next server reminder is due; null unless the state is `link_sent`. */
  next_reminder_due_at: string | null;
  /**
   * FS-789 — the newest verification email an operator sent with the Send
   * verification email button (UTC ISO), and the console account that sent
   * it, by email. An admin send is not a reminder: the state and the reminder
   * count above ignore it. Absent on an API that predates the button.
   */
  last_admin_send_at?: string | null;
  last_admin_send_by?: string | null;
}

/**
 * FS-1021 — whether the owner gets HVAC system emails. The app's switch and
 * the unsubscribe link in an email are one setting, kept on the owner's app
 * account in Firestore and read through firebase-api. `unavailable` means it
 * could not be read just now (a timeout, an error, a malformed record), and
 * it is never treated as on: Send stays off until it can be read.
 *
 * It is about the ACCOUNT, not the address. Verification emails and password
 * resets are not covered by it, and the address status and the verification
 * state say nothing about it.
 */
export type EmailPreferenceState = 'on' | 'off' | 'unavailable';

/** The recipient as the DATABASE knows them. Tier and gate come from a draft. */
export interface NeedsRecipient {
  address: string | null;
  /**
   * What SES and the account have said about the ADDRESS. FS-1021: nothing
   * writes `unsubscribed` any more; one left from before is still honoured
   * as an exclusion, and the owner's choice is `email_preference`.
   */
  address_status: string | null;
  /**
   * Plan 123 R13 — the judgement on the address in its own words, who made
   * it and when (UTC ISO). For an unsubscribe-link click the note names the
   * send, the reason code and whatever the homeowner typed; `unsubscribeWhy`
   * says it for a person. Absent on an API that predates R13.
   */
  address_note?: string | null;
  address_judged_by?: string | null;
  address_judged_at?: string | null;
  last_request_at: string | null;
  /** Reasons nothing can be written to this address at all. */
  blockers: Blocker[];
  /** Plan 123 R11 — what to know about the address, which stops nothing. */
  notes?: Blocker[];
  /** FS-646 — absent on an API that predates address verification. */
  verification?: RecipientVerification | null;
  /**
   * FS-1021 — the claim holder's app account. Allow sends it back with the
   * address, and the server refuses the call if the Clip's owner has moved to
   * another account since the page loaded. Null when the Clip has no claim
   * holder; absent on an API that predates FS-1021.
   */
  owner_uid?: string | null;
  /**
   * FS-1021 — the owner's HVAC system emails setting. Null when the Clip has
   * no claim holder, so there is no account to read it from; absent before
   * FS-1021. Neither holds Send by itself.
   */
  email_preference?: { state: EmailPreferenceState } | null;
  /**
   * FS-1021 — the newest unsubscribe-link click on any email sent to this
   * owner (UTC ISO), or null. History only: the setting above is what holds
   * Send, and the owner may have turned emails back on since.
   */
  link_unsubscribed_at?: string | null;
}

export interface EquipmentNeedsResponse {
  device_id: number;
  system_key: string | null;
  version: number;
  score: number;
  clip_count: number;
  siblings_hidden: number;
  clips: NeedsClip[];
  /** Phase 4 additions; absent on a Phase 1 API. */
  system_blockers?: Blocker[];
  /**
   * Plan 123 R11 — what to know about this system before writing to it: an
   * earlier request still in flight, Clips that disagree. Never a reason
   * Send is off. Absent on an API that predates R11.
   */
  system_notes?: Blocker[];
  history?: RequestHistoryRow[];
  /** Only with contact_users. */
  recipient?: NeedsRecipient | null;
  /**
   * Plan 123 R3 §3 — how many blockers were dropped because they belong to a
   * Clip this account cannot see. Absent on an API that predates it.
   */
  hidden_blocker_count?: number;
}

/**
 * The reputation gate's verdict at the moment the draft was rendered.
 *
 * Plan 123 R3 §6 — `bounce` and `complaint` are FRACTIONS (0.04 is four per
 * cent), and they are null when the gate could not read them at all, which is
 * what `reason` starting `metrics_unavailable` says. Nothing here is a
 * percentage until this module formats it.
 */
export interface DraftGate {
  allowed: string[];
  reason: string;
  bounce: number | null;
  complaint: number | null;
}

export interface DraftRecipient {
  address: string | null;
  address_status: string | null;
  tier: string | null;
  eligible: boolean;
  gate: DraftGate | null;
  last_request_at: string | null;
  blockers: Blocker[];
  /** Plan 123 R11 — the draft's own notes about the address. */
  notes?: Blocker[];
  /**
   * FS-1021 — the owner's setting as the renderer read it. Off arrives with
   * an `email_off` blocker and unavailable with `email_preference_unavailable`;
   * a render never stores either. Absent on a service that predates FS-1021.
   */
  email_preference?: EmailPreferenceState;
}

export interface DraftResponse {
  ok: boolean;
  command: string;
  status: number;
  system_key: string | null;
  claim_id: string | null;
  observed_at: string | null;
  device_ids: number[];
  context_status: string | null;
  trigger_device_id: number;
  components: ComponentName[];
  filter_device_ids: number[];
  subject: string;
  body_text: string;
  footer_text: string;
  from_address: string;
  reply_to: string;
  recipient: DraftRecipient;
  /** Plan 123 R3 §3 — blockers the API redacted out of the draft entirely. */
  hidden_blocker_count?: number;
}

export interface SendRequestBody {
  request_id: string;
  components: ComponentName[];
  filter_device_ids: number[];
  system_key: string | null;
  claim_id: string | null;
  observed_at: string | null;
  subject: string;
  body_text: string;
}

export interface SendResponse {
  ok: boolean;
  request_id: string;
  /** `accepted`, `failed` or `unknown` — all three are attempts that happened. */
  state: string;
  send_id?: number;
  to_address?: string | null;
  message_id?: string | null;
  send_error?: string | null;
  replayed?: boolean;
  targets?: Array<{ device_id: number; category: string }>;
}

/** The stored request, read back after a send that did not answer. */
export interface RequestResultResponse {
  request_id: string;
  status: string;
  refusal: string | null;
  to_address?: string | null;
  send: RequestSend | null;
  targets: RequestTarget[];
}

export type ReplyOutcome = 'photo_received' | 'not_applicable' | 'declined';

/** The server's own limits, checked here so the box can count down to them. */
export const SUBJECT_MAX = 200;
export const BODY_MAX = 4000;

const deviceUrl = (deviceId: number) => `${config.devicesApiUrl}/devices/${deviceId}`;

export async function fetchEquipmentNeeds(deviceId: number): Promise<EquipmentNeedsResponse> {
  const res = await axiosInstance.get<EquipmentNeedsResponse>(
    `${deviceUrl(deviceId)}/equipment-needs`,
  );
  return res.data;
}

export async function renderEquipmentDraft(
  deviceId: number,
  body: { components: ComponentName[]; filter_device_ids: number[] },
): Promise<DraftResponse> {
  const res = await axiosInstance.post<DraftResponse>(
    `${deviceUrl(deviceId)}/equipment-request/draft`,
    body,
  );
  return res.data;
}

export async function sendEquipmentRequest(
  deviceId: number,
  body: SendRequestBody,
): Promise<SendResponse> {
  const res = await axiosInstance.post<SendResponse>(
    `${deviceUrl(deviceId)}/equipment-request`,
    body,
  );
  return res.data;
}

/**
 * Read a request back by id. This is the whole recovery path for a send that
 * timed out: the row is there or it is not, and either way no second message
 * is sent finding out.
 */
export async function fetchRequestResult(
  deviceId: number,
  requestId: string,
): Promise<RequestResultResponse> {
  const res = await axiosInstance.get<RequestResultResponse>(
    `${deviceUrl(deviceId)}/equipment-request/${requestId}`,
  );
  return res.data;
}

export async function recordRequestReply(
  deviceId: number,
  requestId: string,
  body: { component: ComponentName; device_id?: number; outcome: ReplyOutcome },
): Promise<{ ok: boolean; rows_written?: number }> {
  const res = await axiosInstance.post<{ ok: boolean; rows_written?: number }>(
    `${deviceUrl(deviceId)}/equipment-request/${requestId}/reply`,
    body,
  );
  return res.data;
}

/** FS-1021 — what Allow answers when the setting was turned back on. */
export interface ResubscribeResponse {
  ok: boolean;
  /**
   * The recipient block as it reads now, or null when that re-read failed.
   * A null here does not undo the change: `ok` stands.
   */
  recipient: NeedsRecipient | null;
}

/**
 * FS-1021 (was Plan 123 R13) — turn the owner's HVAC system emails back on,
 * when the owner asked for that.
 *
 * The body names the address AND the app account the operator was looking
 * at. The server refuses the call if either has moved since the page loaded,
 * and only turns the setting on if it is still off, so a stale page cannot
 * switch emails on for another owner. Nothing about the address changes: its
 * status and its verification stay as they were.
 *
 * Everything that is not a change arrives as an error status with a code
 * (`already_on`, `preference_unavailable`, `refresh_required`,
 * `address_mismatch`), and `resubscribeRefusal` says it.
 *
 * A needs block with no `owner_uid` came from an API too old to bind the
 * account. The uid is then left out rather than invented, and the server
 * answers `refresh_required` without changing anything.
 */
export async function resubscribeAddress(
  deviceId: number,
  address: string,
  uid: string | null | undefined,
): Promise<ResubscribeResponse> {
  const res = await axiosInstance.post<ResubscribeResponse>(
    `${deviceUrl(deviceId)}/equipment-request/resubscribe`,
    uid ? { address, uid } : { address },
  );
  return res.data;
}

/** FS-789 — what the verification route answers when Google took the send. */
export interface VerificationEmailSent {
  outcome: 'sent';
  /** When the send was recorded (UTC ISO). */
  sent_at: string;
  /** The claim holder's address the link went to. */
  address: string;
}

/**
 * FS-789 — ask Google to send the claim holder the standard MyFluxHive
 * verification link. The body names the address the operator was looking
 * at; the server refuses it if the claim holder's address has moved since.
 * Everything that is not a send arrives as an error status with a code, and
 * `verificationEmailRefusal` says it.
 */
export async function sendVerificationEmail(
  deviceId: number,
  address: string,
): Promise<VerificationEmailSent> {
  const res = await axiosInstance.post<VerificationEmailSent>(
    `${deviceUrl(deviceId)}/verification-email`,
    { address },
  );
  return res.data;
}

/**
 * The id that makes a send safe to repeat. Minted when the confirm dialog
 * opens, kept for every attempt at that one request.
 */
export const newRequestId = (): string =>
  globalThis.crypto?.randomUUID?.()
  ?? `req-${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`;

/** A refusal the server sent, as its code and its sentence. */
export interface RequestRefusal {
  code: string;
  message: string;
}

/**
 * FS-1021 — the two sentences every surface uses for the owner's setting
 * (wording approved by Chris, 2026-10-16). The first is what any message
 * about `email_off` says; the second is what an unreadable setting says.
 */
export const EMAIL_OFF_TEXT = 'This owner has turned off HVAC system emails.';
export const EMAIL_PREFERENCE_UNAVAILABLE_TEXT =
  "This owner's email setting couldn't be read. Try again in a moment.";

/**
 * Plan 123 Phase 4b — one plain sentence per refusal code.
 *
 * The reader is a FluxHive admin who knows HVAC and homeowners and does not
 * know our code, so each sentence says what happened and who does the next
 * thing: the homeowner, us, or nobody. The code itself is shown in
 * parentheses after the sentence, for a support conversation, never on its
 * own.
 *
 * R6: this table is the vocabulary of an ATTEMPT THAT HAPPENED — a Send the
 * operator clicked, and the answer that came back. It is written in the past,
 * and several sentences end "nothing was sent", which is a report on the fate
 * of a message. It is reached from `requestRefusal` and from the history, and
 * from nowhere else. The same codes arriving BEFORE any attempt are blockers,
 * and `BLOCKER_TEXT` says them in the present.
 *
 * These are the codes the Lambda and the API can send: the send refusals
 * (EQUIPMENT_REQUEST_CONTRACT "Refusal codes"), the recipient and system
 * blockers the needs endpoint reads out of the database, and the two
 * per-target ones.
 */
export const REFUSAL_TEXT: Record<string, string> = {
  // --- the address -------------------------------------------------------
  no_owner_email: 'There is nobody to write to: this home has no email address on file.',
  no_address: 'There is nobody to write to: this home has no email address on file.',
  address_undeliverable:
    'Mail to this address bounced before, so nothing else is sent to it. FluxHive needs a new address for this home.',
  undeliverable:
    'Mail to this address bounced before, so nothing else is sent to it. FluxHive needs a new address for this home.',
  // FS-1021: an `unsubscribed` address status is left over from the old
  // unsubscribe link, which nothing writes any more. It still holds, so it
  // still has words, scoped to what the opt-out covers.
  address_unsubscribed: 'This homeowner unsubscribed. No HVAC system emails are sent to them.',
  unsubscribed: 'This homeowner unsubscribed. No HVAC system emails are sent to them.',
  // FS-1021: the owner's own setting, from the app's switch or the link.
  email_off: EMAIL_OFF_TEXT,
  // A render reports this as a blocker and stores nothing; a send that could
  // not read the setting fails before any record is written, so it reaches
  // the screen as an attempt nobody can account for, not under this code.
  email_preference_unavailable: EMAIL_PREFERENCE_UNAVAILABLE_TEXT,
  // R8 (Chris, 2026-09-21): an internal FluxHive address is an ordinary
  // verified-tier recipient, so there is no `internal` refusal to word.
  // R7: operator sends go to unconfirmed and relay addresses too, so the
  // only tier rule left is the reputation gate's per-tier allowance. The
  // refusal now reports that gate rather than a confirmed-only rule that no
  // longer exists. Which tiers the gate was admitting is the gate line in
  // "Who this goes to", which has the allowance; the refusal does not.
  tier_ineligible:
    'The reputation gate was not admitting this address type, so nothing was sent.',
  // R3 F26: the gate closes on a high rate AND on not being able to read the
  // rates at all, so the refusal itself does not know which. The gate line in
  // "Who this goes to" says which, because it has the reason.
  reputation_gate:
    'Sending is paused for addresses like this one: the mail-reputation gate is closed to them right now. Nothing was sent.',
  sending_disabled: 'Sending is turned off on this deployment. Nothing was sent.',
  // FS-646: with verified-only sending on, the gate admits only confirmed
  // and internal addresses. The verification line in "Who this goes to" says
  // where confirming this one has got to.
  address_not_verified: 'This address has not been confirmed yet. Nothing was sent.',

  // --- the home and its records -----------------------------------------
  // R3 F27: the Lambda raises `no_claim` for a missing claim, for more than
  // one, and for one too old to trust. "There is no account" is only the
  // first of those, so the sentence names all three.
  no_claim:
    'No usable app account is registered to this Clip (none, more than one, or out of date).',
  claim_missing:
    'No app account is registered to this Clip, so nothing about this system can be trusted yet.',
  claim_ambiguous:
    'More than one app account is registered to this Clip. FluxHive has to settle which one is current.',
  claim_stale:
    'The app account registered to this Clip is out of date. FluxHive has to refresh it.',
  claim_not_current:
    'The app account registered to this Clip is not the current one. FluxHive has to refresh it.',
  // R3 F27: the server raises this at MORE THAN TWO DAYS old, so telling an
  // operator to wait for tonight is telling them to wait for something that
  // has already not happened twice. R4 F32: the age does not establish what
  // tonight will do either, so the sentence says the age and stops. The line
  // on screen carries a link to the Inventory, which is where it is looked at.
  projection_stale:
    "This home's summary is more than two days old and needs a look.",
  // R3 F27: the marker moves when the OBSERVATION moves, not only when the
  // household does, so "the owner changed" claims more than the refusal knows.
  recipient_changed:
    'The owner or the record for this system was re-read and differs from the draft; reload and draft again.',
  membership_changed:
    'A Clip moved off this system since the draft was made. Reload the tab and draft the message again.',
  system_changed:
    'The system this message was written for was re-read and differs from the draft; reload and draft again.',
  sibling_out_of_scope:
    'This system has a Clip outside what you can see, so the message cannot be sent from here.',

  // --- the request itself ------------------------------------------------
  request_not_found: 'No request with that reference belongs to this Clip.',
  target_not_requested: 'That part was not part of the request being replied to.',
  no_send_to_reply_to: 'That request never reached a send, so there is no reply to record.',
  invalid_target: 'That is not a part this tab can ask about.',
  invalid_outcome: 'That is not a reply this tab can record.',
  invalid_request:
    'The request was not put together correctly, so nothing was sent. Reload the tab and try again.',

  // --- the plumbing ------------------------------------------------------
  // R5: this wording is about a SEND — it is what a stored refusal on a
  // request row says. The draft and the reply routes have their own
  // sentences (`DRAFT_HOP_FAILED_TEXT`, `REPLY_HOP_FAILED_TEXT`), because
  // neither of them was sending anything.
  lambda_hop_failed:
    'The sending service did not answer, so nothing was sent. Try again in a minute.',
  // --- refusals no release raises any more (R11) -------------------------
  //
  // Plan 123 R11 stopped every one of these from refusing anything: they are
  // NOTES now (`NOTE_TEXT`). The wording stays because the history table is a
  // record of attempts that were really refused, under the rule of their day,
  // and a row written in August still has to read as something. Each sentence
  // is past tense, says the rule is gone, and tells an operator what a fresh
  // one would mean: the sending service is still on the old release.
  cooldown:
    'Refused as a repeat ask. Repeat asks are allowed now, so this means the sending service is still on the old release.',
  reply_terminal:
    'Refused because the homeowner had already answered. A new request is allowed now, so this means the sending service is still on the old release.',
  unresolved_attempt:
    'Refused because an earlier message to this home never reported back. That is a note now, not a refusal.',
  abandoned_attempt:
    'Refused because an earlier message to this home was given up on. That is a note now, not a refusal.',
  target_not_needed:
    'Refused because a part asked about was not missing. Any part of the system can be asked for now.',
  address_test: 'Refused because the address is a test account address. A test address is written to now.',
  test: 'Refused because the address is a test account address. A test address is written to now.',
  projections_disagree:
    'Refused because the Clips in this home do not agree about the system. That is a note now, not a refusal.',
  shared_record_multiple_filters:
    'Refused because two filter sizes are recorded on one system. That is a note now, not a refusal.',
  no_response:
    'Nothing came back from the server. The message may still have gone out, so it is being checked rather than sent again.',
  unauthorized: 'Your account cannot write to homeowners.',
};

/**
 * Plan 123 R6 — the second vocabulary: why Send is unavailable, before
 * anybody has clicked it.
 *
 * On device 216 an operator ticked two boxes, sent nothing, and read this in
 * "Who this goes to" (the tier sentence is quoted in the wording of that day;
 * R7 dropped the confirmed-only rule it describes):
 *
 *   We only write to addresses that are confirmed ... This one is not
 *   confirmed, so nothing was sent.
 *   Sending is turned off on this deployment. Nothing was sent.
 *
 * Both sentences came out of `REFUSAL_TEXT`, which answers "what happened to
 * the message I sent". Nothing had been sent, so the block reported the fate
 * of a message that never existed, and it read as the tab having written to
 * the homeowner on its own.
 *
 * A BLOCKER is the other state: a condition the needs endpoint and the draft
 * report while the boxes are still being ticked. Nothing has been attempted,
 * so there is no fate to report and nothing to repeat. Every sentence here is
 * therefore in the present, opens `Send is unavailable` so the list reads as
 * reasons one control is off, says what would clear it where an operator can
 * act, and never says "nothing was sent" or "try again".
 *
 * Every code that can arrive as a blocker has an entry. The reply-route and
 * plumbing codes (`invalid_*`, `request_not_found`, `lambda_hop_failed`,
 * `no_response`, `unauthorized`) cannot: they only exist as the answer to a
 * call that was made, so they stay refusals.
 */
export const BLOCKER_TEXT: Record<string, string> = {
  // --- the address -------------------------------------------------------
  no_owner_email: 'Send is unavailable: this home has no email address on file.',
  no_address: 'Send is unavailable: this home has no email address on file.',
  address_undeliverable:
    'Send is unavailable: mail to this address bounced before, so FluxHive does not write to it. This home needs a new address.',
  undeliverable:
    'Send is unavailable: mail to this address bounced before, so FluxHive does not write to it. This home needs a new address.',
  address_unsubscribed: 'Send is unavailable: this homeowner unsubscribed from HVAC system emails.',
  unsubscribed: 'Send is unavailable: this homeowner unsubscribed from HVAC system emails.',
  // FS-1021: the owner's setting. The sentence is `EMAIL_OFF_TEXT` behind the
  // opener every blocker shares. The unreadable one stops short of "try
  // again", which no blocker says (R6); the line about the setting in "Who
  // this goes to" says it.
  email_off: 'Send is unavailable: this owner has turned off HVAC system emails.',
  email_preference_unavailable: "Send is unavailable: this owner's email setting couldn't be read.",
  // R8 (Chris, 2026-09-21): nothing about an internal FluxHive address holds
  // a send back — it tiers as verified and is sent to like any other address
  // — so there is no internal blocker to word. A code an older deployment
  // still raises falls to the sentence every unknown code gets.
  // R7: the confirmed-only rule is gone. What can still hold a tier back is
  // the reputation gate's allowance, which is what this now says. The
  // allowance itself is on the gate line, which reads it from the gate.
  tier_ineligible:
    'Send is unavailable: the reputation gate is not admitting this address type right now.',
  // The gate closes on a rate being too high AND on the rates being
  // unreadable, and the blocker's own message is the gate's reason, so the
  // two are told apart from it rather than guessed (R3 F26, R6).
  reputation_gate:
    'Send is unavailable: sending is paused because of recent bounces or complaints.',
  reputation_gate_metrics_unavailable:
    'Send is unavailable: sending is paused because FluxHive could not read its mail-reputation numbers.',
  sending_disabled: 'Send is unavailable: sending is turned off on this deployment.',
  address_not_verified: 'Send is unavailable: this address has not been confirmed yet.',

  // --- the home and its records -----------------------------------------
  no_claim:
    'Send is unavailable: no usable app account is registered to this Clip (none, more than one, or out of date).',
  claim_missing: 'Send is unavailable: no app account is registered to this Clip.',
  claim_ambiguous:
    'Send is unavailable: more than one app account is registered to this Clip, and FluxHive has to settle which one is current.',
  claim_stale:
    'Send is unavailable: the app account registered to this Clip is out of date, and FluxHive has to refresh it.',
  claim_not_current:
    'Send is unavailable: the app account registered to this Clip is not the current one, and FluxHive has to refresh it.',
  projection_stale:
    "Send is unavailable: this home's summary is more than two days old and needs a look.",
  recipient_changed:
    'Send is unavailable: the owner or record changed since the draft. Reload the tab.',
  system_changed:
    'Send is unavailable: the system changed since the draft. Reload the tab.',
  membership_changed:
    'Send is unavailable: a Clip moved off this system since the draft. Reload the tab.',
  sibling_out_of_scope:
    'Send is unavailable: this system has a Clip outside what you can see.',
};

/** The four words every blocker sentence opens with. */
const BLOCKER_OPENER = 'Send is unavailable';

/**
 * Codes whose sentence is only useful with the server's own specifics — the
 * two dates on a cooldown, the outcome on an answered target. For those the
 * server's message wins; everywhere else the wording above does, because the
 * server writes for whoever is reading the log. When the server sends the
 * dates as timestamps rather than prose, `datedBlockerSentence` writes them
 * in Eastern and neither of these is used (R2 F17).
 */
const PREFER_SERVER_MESSAGE = new Set(['cooldown', 'reply_terminal']);

/**
 * Plan 123 R3 F27 — a bare calendar date in a server sentence.
 *
 * Every date on this tab is Eastern (§11), and a date the SERVER composed is
 * a UTC calendar day: `until.isoformat()` in the Lambda, which at 21:00
 * Eastern in January names tomorrow. So a server sentence is only preferred
 * while it names no date; one that does is replaced by our own wording, and
 * the date comes back through `since`/`until` in Eastern. A server sentence
 * with a date is still shown when there is nothing else at all — a code this
 * console has never heard of — because saying little is worse than saying it
 * in the wrong zone.
 */
const BARE_DATE = /\d{4}-\d{2}-\d{2}/;

const plainRefusal = (code: string, serverMessage?: string | null): string => {
  // An empty message is no message: without this, an unknown code arriving
  // with `message: ''` printed an empty sentence.
  const sent = serverMessage && serverMessage.trim() !== '' ? serverMessage : undefined;
  const undated = sent && !BARE_DATE.test(sent) ? sent : undefined;
  return (
    (PREFER_SERVER_MESSAGE.has(code) ? undated : undefined)
    ?? own(REFUSAL_TEXT, code)
    ?? undated
    ?? sent
    ?? 'The request was refused, and nothing was sent.'
  );
};

/**
 * A refusal or blocker as one plain sentence, with the code in parentheses
 * for support. An unknown code still gets a sentence: the server's own, or a
 * last-resort one that says nothing was sent.
 */
export function refusalSentence(
  code: string | null | undefined,
  serverMessage?: string | null,
  options: { withCode?: boolean } = {},
): string {
  const key = code ?? 'unknown';
  const plain = plainRefusal(key, serverMessage);
  return options.withCode === false ? plain : `${plain} (${key})`;
}

/**
 * Plan 123 R3 §3 — the sentence the API substitutes for a blocker raised by a
 * Clip the caller cannot see. It carries no id and no date, and it is the
 * API's wording rather than a code, so it is shown exactly as it arrived.
 */
export const REDACTED_BLOCKER_MESSAGE =
  'A Clip on this system you cannot see has a pending or answered request.';

/**
 * The part a categorised blocker is about, in a phrase (§3).
 *
 * R4 F31: these are the WIRE categories the Lambda writes (FS-838 adds the coil)
 * (`CATEGORIES_FOR_COMPONENT`). A packaged machine is asked about as both
 * photos rather than as a category of its own, and `hvac_photo` is a field name
 * on the inventory, not a category — keying on it left every furnace blocker
 * unnamed.
 */
const CATEGORY_PHRASE: Record<string, string> = {
  ac_photo: 'the outdoor unit',
  furnace_photo: 'the furnace or air handler',
  // FS-838: the coil request's own wire category.
  coil_photo: 'the indoor coil',
  filter_photo: 'the filter',
};

/**
 * Which part a blocker is about, and on which Clip when it names one. The
 * server's own message never embeds the id, so this is the only place it is
 * composed.
 *
 * R4 F31: a SHARED hold carries no device id on purpose — one cooldown for
 * the whole system, whichever Clip reported it — and saying nothing for it
 * dropped the part out of a dated sentence. The category still names the
 * part, so it is named without inventing a Clip to hang it on. A category
 * this console does not know names nothing at all.
 */
export const blockerTargetPhrase = (blocker: Blocker): string | null => {
  const phrase = own(CATEGORY_PHRASE, blocker.category);
  const deviceId = blocker.device_id;
  if (deviceId === null || deviceId === undefined) return phrase ?? null;
  return `${phrase ?? 'this part'} on device ${deviceId}`;
};

const capitalise = (value: string): string =>
  (value ? value.charAt(0).toUpperCase() + value.slice(1) : value);

/**
 * Plan 123 R2 F17, R3 §3 — a REFUSAL whose sentence is a date, in Eastern.
 *
 * The server composes `Asked on 2026-01-15; askable again on 2026-02-05` off
 * the UTC calendar day, and every other time on this console is Eastern: at
 * 21:00 Eastern in January the server's sentence names tomorrow. When the
 * blocker carries the instants themselves the sentence is written here
 * instead, in Eastern, with the clock time on the expiry so the boundary is
 * the real one rather than a whole day of slack.
 *
 * Cooldown and a terminal reply each read as their own sentence because the
 * date IS the sentence. Every other dated code — a stuck attempt, an address
 * that bounced, a summary that went stale — keeps its own wording and gets
 * the moments appended, so no dated code is left printing UTC (F17 remainder).
 * A timestamp that cannot be read falls back rather than printing a dash.
 *
 * R6: this is the past-tense half. `datedBlockerClause` is the same job in
 * the blocker vocabulary, and the two are kept apart on purpose.
 */
const datedRefusalClause = (code: string, blocker: Blocker): string | null => {
  if (code === 'cooldown') {
    // R11: the wait is gone, so the expiry is not worth a clause — an
    // operator reading a refused row is not waiting for anything. What the
    // date still says is when the earlier ask went out.
    const asked = blocker.since ? easternDate(blocker.since, '') : '';
    if (!asked) return null;
    return `${REFUSAL_TEXT.cooldown} Asked on ${asked}.`;
  }
  if (code === 'reply_terminal' && blocker.since) {
    const replied = easternDate(blocker.since, '');
    if (!replied) return null;
    return `${REFUSAL_TEXT.reply_terminal} They replied on ${replied}.`;
  }
  const since = blocker.since ? easternDate(blocker.since, '') : '';
  const until = blocker.until ? easternDateTime(blocker.until, '') : '';
  if (!since && !until) return null;
  const parts = [plainRefusal(code, blocker.message)];
  if (since) parts.push(`Recorded on ${since}.`);
  if (until) parts.push(`In effect until ${until}.`);
  return parts.join(' ');
};

/**
 * Plan 123 R6 — one blocker's sentence, before the target is named.
 *
 * No server message is read here. Every code that can arrive as a blocker
 * has wording of its own, and the server's message is written for whoever
 * reads the log: it is in the past ("was asked for recently"), it carries UTC
 * dates, and letting it through is how refusal tense got onto a screen where
 * nothing had been attempted. A code this console has never heard of says so
 * and stops, rather than borrowing a sentence about a send.
 */
const plainBlocker = (code: string, blocker: Blocker): string => {
  if (
    code === 'reputation_gate'
    && (blocker.message ?? '').trim().startsWith('metrics_unavailable')
  ) {
    return BLOCKER_TEXT.reputation_gate_metrics_unavailable;
  }
  return own(BLOCKER_TEXT, code) ?? `${BLOCKER_OPENER} (${code}).`;
};

/**
 * The reply the homeowner gave, out of the server's own sentence.
 *
 * The blocker shape carries no outcome field — the Lambda writes it into the
 * message as a trailing parenthetical (`the filter was already answered
 * (declined)`), and that is the only place it exists on the wire. So it is
 * read, never guessed, and a message that carries no recognisable outcome
 * drops the clause instead of inventing a reply.
 */
const REPLY_OUTCOME_PHRASE: Record<string, string> = {
  photo_received: 'photo received',
  not_applicable: 'not on this system',
  declined: 'declined',
};

const replyOutcomePhrase = (message: string | null | undefined): string | null => {
  const outcome = /\(([a-z_]+)\)\s*\.?\s*$/.exec(message ?? '')?.[1];
  return outcome ? REPLY_OUTCOME_PHRASE[outcome] ?? null : null;
};

/**
 * Plan 123 R6 — a blocker whose sentence is a date, in the present.
 *
 * Same Eastern rule and same two special cases as the refusal half, said as
 * a condition that holds rather than as the fate of a message: a cooldown is
 * when the ask can happen, a terminal reply is what the homeowner already
 * said. Every other dated code keeps its blocker wording and gets the moments
 * appended.
 */
const datedBlockerClause = (code: string, blocker: Blocker): string | null => {
  // R11: `cooldown` and `reply_terminal` used to have their own sentence
  // here. Neither is a blocker any more — they are notes, and their dated
  // wording moved to `datedNoteClause`. One arriving from a server that
  // predates R11 falls through to the generic clause below.
  const since = blocker.since ? easternDate(blocker.since, '') : '';
  const until = blocker.until ? easternDateTime(blocker.until, '') : '';
  if (!since && !until) return null;
  const parts = [plainBlocker(code, blocker)];
  if (since) parts.push(`Recorded on ${since}.`);
  if (until) parts.push(`In effect until ${until}.`);
  return parts.join(' ');
};

/**
 * The same sentence, aimed at the one part of the system it holds.
 *
 * A blocker sentence opens `Send is unavailable`, so the part belongs inside
 * that opener — "Send is unavailable for the filter on device 382: ..." —
 * rather than bolted on in front of it. Anything that does not open that way
 * keeps the older form, which is what the refusal half still uses.
 */
const aimAtTarget = (sentence: string, target: string | null): string => {
  if (!target) return sentence;
  return sentence.startsWith(BLOCKER_OPENER)
    ? `${BLOCKER_OPENER} for ${target}${sentence.slice(BLOCKER_OPENER.length)}`
    : `${capitalise(target)}: ${sentence}`;
};

/**
 * Why Send is unavailable, as one plain sentence in the present.
 *
 * No code in parentheses here. A blocker describes a condition the operator
 * can see and act on, and several of the codes are our own record vocabulary
 * (`claim_*`, `projection_stale`) that the sentence exists to keep off the
 * screen. A send REFUSAL is the other case: the server said no to something
 * that was actually tried, support may be asked about it, so that one carries
 * its code and its past tense (`requestRefusal`).
 *
 * A blocker carrying its own timestamps gets the Eastern sentence above, and
 * one carrying a device id or a category says which part it holds. A blocker
 * the API redacted is passed through as it arrived: there is nothing to
 * compose from and nothing this screen is allowed to add.
 */
export const blockerSentence = (blocker: Blocker): string => {
  if (blocker.message?.trim() === REDACTED_BLOCKER_MESSAGE) return REDACTED_BLOCKER_MESSAGE;
  const code = blockerCode(blocker);
  const sentence =
    datedBlockerClause(code, blocker)
    ?? plainBlocker(code, blocker);
  return aimAtTarget(sentence, blockerTargetPhrase(blocker));
};

/**
 * Plan 123 R11 — the third vocabulary: what an operator should KNOW, which
 * stops nothing at all.
 *
 * Chris, 2026-09-22, after his first live send: "I don't like how I can't
 * tick the checkboxes on the missing information form ... I want the admin to
 * be able to drive the requests." Every sentence here used to be a reason a
 * control was off. None of them is now: the history of a part, the state of
 * a part and what the address is are things to tell somebody, not grounds
 * for refusing them.
 *
 * So a note is written like a colleague leaning over: the fact, and what it
 * would mean if you went ahead anyway. It never opens `Send is unavailable`,
 * never says "wait", and never names a date the ask becomes possible again,
 * because there is no such date.
 */
export const NOTE_TEXT: Record<string, string> = {
  cooldown: 'This part was asked for recently.',
  reply_terminal: 'The homeowner has already replied about this one.',
  unresolved_attempt:
    'An earlier request on this system is still in flight or came back unknown. '
    + 'Check status reads it back; sending now may send twice.',
  abandoned_attempt:
    'An earlier request on this system was abandoned and may have been delivered.',
  projections_disagree: 'The Clips in this home do not agree about the system.',
  shared_record_multiple_filters:
    'Two filter sizes are recorded on one system, so which Clip has which filter is not settled.',
  address_test: 'This is a test account address.',
  test: 'This is a test account address.',
  documented:
    'Already on file. Asking again replaces nothing; the homeowner\'s photo would be checked '
    + 'against what is there.',
  not_applicable: 'Declared not on this system. Asking anyway is fine if that was wrong.',
  // FS-838: why a part that owns no fact reads Documented. The tonnage ask
  // goes to one unit at a time, furnace or air handler first, then the coil.
  capacity_from_indoor_unit:
    'The furnace or air handler is asked for this system\'s size first, so nothing is needed '
    + 'from this part for it.',
  capacity_from_indoor_coil:
    'A furnace\'s label gives only an "up to" size, so the indoor coil is asked for this '
    + 'system\'s size first; nothing is needed from this part for it unless the coil gives nothing.',
};

/**
 * Codes whose note is worth more in the server's words than in ours: the
 * server knows the VALUE already on file, and which reason a part is held
 * for. Ours is the fallback for an API that sends the code and no sentence.
 */
const PREFER_SERVER_NOTE = new Set(['documented', 'not_applicable', 'blocked']);

const plainNote = (code: string, note: Blocker): string => {
  const sent = note.message && note.message.trim() !== '' ? note.message.trim() : undefined;
  // Same rule as everywhere else on this tab: a date the SERVER composed is a
  // UTC calendar day, and this console is Eastern (§11).
  const undated = sent && !BARE_DATE.test(sent) ? sent : undefined;
  return (
    (PREFER_SERVER_NOTE.has(code) ? undated : undefined)
    ?? own(NOTE_TEXT, code)
    ?? undated
    ?? `Worth knowing about this part (${code}).`
  );
};

const EASTERN_DAY_MS = 86400000;

/**
 * How long ago a note's moment was, in Eastern calendar days.
 *
 * "Asked on 2026-09-21" told Chris nothing he could act on; "(1 day ago)" is
 * the part he was actually weighing when he decided whether to write again.
 * Both ends are the Eastern day, so a 23:30 UTC ask is not a day older than
 * it looks to somebody in New York.
 */
export const daysAgoPhrase = (
  since: string | null | undefined,
  now: Date = new Date(),
): string | null => {
  const then = easternDate(since, '');
  const today = easternDate(now, '');
  if (!then || !today) return null;
  const days = Math.round(
    (Date.parse(`${today}T00:00:00Z`) - Date.parse(`${then}T00:00:00Z`)) / EASTERN_DAY_MS,
  );
  if (!Number.isFinite(days) || days < 0) return null;
  if (days === 0) return 'today';
  return days === 1 ? '1 day ago' : `${days} days ago`;
};

/** A note whose sentence IS its date, written in Eastern. */
const datedNoteClause = (code: string, note: Blocker, now?: Date): string | null => {
  if (code === 'cooldown' && note.since) {
    const asked = easternDate(note.since, '');
    if (!asked) return null;
    const ago = daysAgoPhrase(note.since, now);
    return ago ? `Asked on ${asked} (${ago}).` : `Asked on ${asked}.`;
  }
  if (code === 'reply_terminal' && note.since) {
    const replied = easternDate(note.since, '');
    if (!replied) return null;
    const outcome = replyOutcomePhrase(note.message);
    return outcome ? `Replied '${outcome}' on ${replied}.` : `Replied on ${replied}.`;
  }
  const since = note.since ? easternDate(note.since, '') : '';
  if (!since) return null;
  return `${plainNote(code, note)} Recorded on ${since}.`;
};

/**
 * One note as a sentence, aimed at the part it is about.
 *
 * The same structured fields a blocker carries — the Clip, the category, the
 * moments — say which filter on which Clip a note belongs to, and a note the
 * API redacted is passed through exactly as it arrived.
 */
export const noteSentence = (note: Blocker, now?: Date): string => {
  if (note.message?.trim() === REDACTED_BLOCKER_MESSAGE) return REDACTED_BLOCKER_MESSAGE;
  const code = blockerCode(note);
  const sentence = datedNoteClause(code, note, now) ?? plainNote(code, note);
  const target = blockerTargetPhrase(note);
  return target ? `${capitalise(target)}: ${sentence}` : sentence;
};

/**
 * The same blocker said as a REFUSAL, for the one caller that has one: a
 * Send that was clicked and came back with the blocker behind its refusal
 * (R4 F31). Nothing on the pre-attempt screen reaches this.
 */
const refusalFromBlocker = (blocker: Blocker): string => {
  if (blocker.message?.trim() === REDACTED_BLOCKER_MESSAGE) return REDACTED_BLOCKER_MESSAGE;
  const code = blockerCode(blocker);
  const sentence =
    datedRefusalClause(code, blocker)
    ?? plainRefusal(code, blocker.message);
  const target = blockerTargetPhrase(blocker);
  return target ? `${capitalise(target)}: ${sentence}` : sentence;
};

/**
 * What went wrong, as the code and one plain sentence for it.
 *
 * The sentence comes from `REFUSAL_TEXT` rather than from the server, because
 * the operator's next step differs by code and the wording has to be the same
 * whichever route refused: `cooldown` is "wait", `recipient_changed` is
 * "draft it again", `unresolved_attempt` is "settle the stuck one". A
 * response that never arrived is its own case, and it is NOT a refusal — the
 * send may well have happened.
 */
export function requestRefusal(err: unknown): RequestRefusal {
  const response = (err as { response?: { status?: number; data?: any } } | null)?.response;
  if (!response) {
    return { code: 'no_response', message: REFUSAL_TEXT.no_response };
  }
  const data = response.data ?? {};
  const code = data.code ?? data.refusal ?? `http_${response.status ?? 'error'}`;
  if (response.status === 403 && !own(REFUSAL_TEXT, code)) {
    return { code, message: 'Your account cannot write to homeowners.' };
  }
  /**
   * R4 F31 — a refusal can carry the blocker behind it, with the part, the
   * Clip and the two instants on it. It is the same shape the needs endpoint
   * sends, so it is read by the same fields: a cooldown refusal keeps its
   * Eastern expiry instead of collapsing to "recently", and the API's own
   * redaction of a blocker it must not show passes through as it arrived.
   *
   * R6: read in the REFUSAL vocabulary, not the blocker one. This is the
   * answer to a Send somebody clicked, so it says what happened to it.
   */
  const carried = data.blocker;
  if (carried && typeof carried === 'object') {
    return {
      code,
      message: refusalFromBlocker({
        ...carried,
        code: carried.code ?? carried.refusal ?? code,
        message: carried.message ?? data.message ?? '',
      }),
    };
  }
  return { code, message: refusalSentence(code, data.message, { withCode: false }) };
}

/**
 * Plan 123 R5 — a hop failure costs a different thing on each of the three
 * routes, so it cannot have one sentence.
 *
 * `lambda_hop_failed`, and a 5xx the API returned with no application code of
 * its own, both say the same thing about the plumbing: the request service
 * was reached and it errored. What that leaves behind is not the same. A SEND
 * is left in doubt — the Lambda may have called SES before it fell over —
 * which is why that path reads the request back instead of writing a sentence
 * at all. A DRAFT and a REPLY were never sending anything, so the send
 * wording is wrong twice over on them: it names a send that did not happen,
 * and "try again in a minute" calls a service error a wait.
 */
export const DRAFT_HOP_FAILED_TEXT =
  'The draft could not be written: the request service returned an error. '
  + 'Nothing was sent. If this keeps happening, FluxHive has to look at the service.';

export const REPLY_HOP_FAILED_TEXT =
  'The reply could not be recorded: the request service returned an error.';

/**
 * True when the call reached the API and the request service behind it
 * errored: a 502 carrying `lambda_hop_failed`, or any 5xx with no refusal
 * code at all. A call that never reached a response is NOT one of these —
 * nothing establishes that it got as far as the service — and a 5xx that did
 * carry a code is that code's own case, with its own sentence.
 */
export const isHopFailure = (err: unknown): boolean => {
  const response = (err as { response?: { status?: number; data?: any } } | null)?.response;
  const status = response?.status;
  if (typeof status !== 'number' || status < 500) return false;
  const code = response?.data?.code ?? response?.data?.refusal;
  return typeof code === 'string' && code.trim() !== '' ? code === 'lambda_hop_failed' : true;
};

/** What went wrong, said the way the DRAFT route has to say it. */
export function draftRefusal(err: unknown): RequestRefusal {
  const refusal = requestRefusal(err);
  return isHopFailure(err) ? { code: refusal.code, message: DRAFT_HOP_FAILED_TEXT } : refusal;
}

/** What went wrong, said the way the REPLY route has to say it. */
export function replyRefusal(err: unknown): RequestRefusal {
  const refusal = requestRefusal(err);
  return isHopFailure(err) ? { code: refusal.code, message: REPLY_HOP_FAILED_TEXT } : refusal;
}

/**
 * Plan 123 R13, FS-1021 — what the Allow button's route answers when it did
 * not turn the setting on. Nothing is sent on this route either way, so its
 * sentences say what happened to the setting and what the operator does next.
 *
 * Two of them come with a reload (`ALLOW_ANSWERS_THAT_RELOAD`): the setting
 * was already on, or the page was too old to name the owner's account. The
 * tab reads itself again, so the sentence says it has.
 */
export const RESUBSCRIBE_REFUSAL_TEXT: Record<string, string> = {
  // FS-1021: the server compares the app account as well as the address.
  address_mismatch:
    "The claim holder's address or app account has changed since this page loaded. Reload and try again.",
  already_on:
    'HVAC system emails were already on for this owner, so nothing changed. The tab has been reloaded.',
  // The server could not confirm the change. It may or may not have been made;
  // trying again is safe, because the server only turns on a setting that is
  // still off and answers `already_on` otherwise.
  preference_unavailable: "Couldn't confirm. Try again.",
  refresh_required:
    'This page was out of date, so the tab has been reloaded. Check the owner shown now before '
    + 'allowing HVAC system emails again.',
};

/** The two answers after which the tab reloads the needs and the draft. */
export const ALLOW_ANSWERS_THAT_RELOAD = new Set(['already_on', 'refresh_required']);

/**
 * What went wrong, said the way the RESUBSCRIBE route has to say it.
 *
 * FS-1021: a call that never answered, and a 5xx carrying no code, leave the
 * change as uncertain as `preference_unavailable` does, so they say the same
 * thing. The shared send wording would talk about a message going out.
 */
export function resubscribeRefusal(err: unknown): RequestRefusal {
  const refusal = requestRefusal(err);
  const ours = own(RESUBSCRIBE_REFUSAL_TEXT, refusal.code);
  if (ours) return { code: refusal.code, message: ours };
  if (isTimeout(err) || isHopFailure(err)) {
    return { code: refusal.code, message: RESUBSCRIBE_REFUSAL_TEXT.preference_unavailable };
  }
  return refusal;
}

/**
 * FS-789 — what the Send verification email button answers, one plain
 * sentence per code. The console owns this wording; the API's own `message`
 * is shown only for a code that is not here.
 *
 * Each sentence says whether the email went out. Two of them cannot: a
 * Lambda that errored may have called Google before it failed to record the
 * result, and a call that never answered may have reached it. Those say it is
 * not known rather than "nothing was sent".
 *
 * A key ending `_recorded` is the same code when the click also recorded that
 * the owner is confirmed (`verified_recorded`), which the send gate reads.
 */
export const VERIFICATION_EMAIL_TEXT: Record<string, string> = {
  // --- refused by device-data-api -----------------------------------------
  no_claim_holder:
    'No app account is registered to this Clip, so there is nobody to send a verification email to.',
  address_mismatch:
    "The owner's address has changed since this page loaded, so nothing was sent. Reload and try again.",
  invalid_request:
    'The request was not put together correctly, so nothing was sent. Reload the tab and try again.',
  // --- refused by the Lambda, after reading the account ---------------------
  no_account: 'The app account registered to this Clip no longer exists, so nothing was sent.',
  account_disabled: 'The app account registered to this Clip is disabled, so nothing was sent.',
  no_email: 'The app account registered to this Clip has no email address, so nothing was sent.',
  email_changed:
    "The owner's sign-in email is not the address shown here, so nothing was sent. Reload the tab. "
    + "If it happens again, the account's sign-in email and its app profile differ, and the account "
    + 'has to be corrected first.',
  not_password_account:
    'This owner signs in with Google or Apple, which confirms the address, so no link is needed. '
    + 'Nothing was sent.',
  not_password_account_recorded:
    'This owner signs in with Google or Apple, which confirms the address, so no link is needed. '
    + 'Nothing was sent. That is recorded now, so homeowner emails can go to this address.',
  already_verified: 'This owner has already confirmed the address, so nothing was sent.',
  already_verified_recorded:
    'This owner has already confirmed the address, so nothing was sent. '
    + 'That is recorded now, so homeowner emails can go to this address.',
  // Google's own limit. FluxHive keeps no limit of its own (the operator drives).
  sent_recently:
    'Google is limiting verification emails to this account right now, so nothing was sent. '
    + 'Try again later.',
  // --- Google or the plumbing -----------------------------------------------
  // `verificationEmailRefusal` adds Google's own error code after this one.
  send_failed: 'Google did not send the email.',
  verification_busy: 'The verification service is busy, so nothing was sent. Try again in a minute.',
  lambda_hop_failed:
    'The verification service returned an error, so it is not known whether the email went out.',
  no_response: 'Nothing came back from the server, so it is not known whether the email went out.',
};

/** The last resort: a code this console does not know, and no message with it. */
const VERIFICATION_EMAIL_FALLBACK = 'The server did not say what happened to the verification email.';

/** A verification-email answer that was not a send, said for the operator. */
export interface VerificationEmailRefusal extends RequestRefusal {
  /** True when this click recorded that the owner is confirmed. */
  verified_recorded: boolean;
}

/**
 * What the Send verification email button answered when it did not send:
 * the code, our sentence for it (then the server's, then a last resort), and
 * whether the click recorded the owner as confirmed, which is the tab's cue
 * to reload the Verification line.
 */
export function verificationEmailRefusal(err: unknown): VerificationEmailRefusal {
  const response = (err as { response?: { status?: number; data?: any } } | null)?.response;
  if (!response) {
    return { code: 'no_response', message: VERIFICATION_EMAIL_TEXT.no_response, verified_recorded: false };
  }
  const data = response.data ?? {};
  const given = typeof data.code === 'string' ? data.code.trim() : '';
  const code = given || `http_${response.status ?? 'error'}`;
  const verifiedRecorded = data.verified_recorded === true;
  const serverMessage =
    typeof data.message === 'string' && data.message.trim() !== '' ? data.message.trim() : undefined;
  const ours = (verifiedRecorded ? own(VERIFICATION_EMAIL_TEXT, `${code}_recorded`) : undefined)
    ?? own(VERIFICATION_EMAIL_TEXT, code);
  let message = ours
    ?? (response.status === 403 ? 'Your account cannot write to homeowners.' : undefined)
    ?? serverMessage
    ?? VERIFICATION_EMAIL_FALLBACK;
  const googleCode = typeof data.google_code === 'string' ? data.google_code.trim() : '';
  if (code === 'send_failed' && googleCode) {
    message = `${message} Google's error code: ${googleCode}.`;
  }
  return { code, message, verified_recorded: verifiedRecorded };
}

/** FS-789 — the line beside the button after Google took the send. Eastern time. */
export const verificationEmailSentSentence = (
  address: string,
  sentAt: string | null | undefined,
): string => {
  const at = easternDateTime(sentAt, '');
  return at
    ? `Verification email sent to ${address} at ${at}.`
    : `Verification email sent to ${address}.`;
};

/** The unsubscribe page's four reasons, said for a person. */
export const UNSUBSCRIBE_REASON_TEXT: Record<string, string> = {
  no_more: 'does not want these emails',
  already_sent: 'already sent the photos',
  not_mine: 'says this is not their system',
  other: 'another reason',
};

/**
 * Plan 123 R13 — the unsubscribe note, for a person.
 *
 * The note is our own Lambda's text (`unsubscribed via link from send 42
 * (…); reason: already_sent; note: …; was: …; one-click`), so it is kept
 * verbatim except for the reason CODE, which becomes words. Only the FIRST
 * `reason:` slot is replaced — the Lambda writes it before the homeowner's
 * own `note:`, and anything in their note stays as they wrote it.
 */
export function unsubscribeWhy(note: string | null | undefined): string | null {
  if (!note || !note.trim()) return null;
  return note.replace(
    /(reason: )(no_more|already_sent|not_mine|other)\b/,
    (_m, lead: string, code: string) => `${lead}${UNSUBSCRIBE_REASON_TEXT[code]}`,
  );
}

/** True when a failed call never reached a response, so the result must be read back. */
export const isTimeout = (err: unknown): boolean =>
  !(err as { response?: unknown } | null)?.response;

/**
 * Plan 123 R2 F11, R3 §5 — what actually proves a message did not go out.
 *
 * device-data-api turns an invoke exception or a Lambda `FunctionError` into
 * a 502, a gateway timeout arrives as a 504, and an overloaded hop answers
 * 503. By then the Lambda may already have written its row and called SES, so
 * none of them says the message was not sent — they say nobody knows. Same
 * for a call that never reached a response at all, and for a 500.
 *
 * So the test is not a list of bad statuses. ONE thing is a definitive
 * refusal: a 4xx carrying the application's own refusal code, which is the
 * shape the API gives a request it declined before dispatch. Everything else
 * is ambiguous and is read back by request id, which sends nothing.
 *
 * R2 excluded 503 from the ambiguous list and released the request id on it;
 * nothing proves a 503 happens before dispatch, and releasing the id is what
 * lets a second message go out (R3 F25).
 */
export const isDefinitiveRefusal = (err: unknown): boolean => {
  const response = (err as { response?: { status?: number; data?: any } } | null)?.response;
  const status = response?.status;
  if (typeof status !== 'number' || status < 400 || status >= 500) return false;
  const data = response?.data ?? {};
  const code = data.code ?? data.refusal;
  return typeof code === 'string' && code.trim() !== '';
};

export const isAmbiguousSendOutcome = (err: unknown): boolean => !isDefinitiveRefusal(err);
